import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { VacancyCriterion } from '@/types/domain';

import {
  fetchVacancyCriteria,
  saveVacancyCriteria,
} from '@/api/queries';
import { safeAlert, safeHaptic } from '@/lib/telegramSafe';

export default function CriteriaScreen(): JSX.Element {
  const { vacancyId } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['criteria', vacancyId],
    queryFn: () => fetchVacancyCriteria(vacancyId!),
    enabled: !!vacancyId,
  });
  const [criteria, setCriteria] = useState<VacancyCriterion[]>([]);
  const [minScore, setMinScore] = useState(6);

  useEffect(() => {
    if (data) {
      setCriteria(data.criteria);
      setMinScore(data.min_score);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      saveVacancyCriteria(vacancyId!, { criteria, min_score: minScore }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['criteria', vacancyId] });
      safeHaptic('success');
    },
    onError: (err: Error) => {
      safeHaptic('error');
      safeAlert(`Не вдалося зберегти: ${err.message}`);
    },
  });

  if (!vacancyId) return <div className="p-4">Невірний id вакансії.</div>;
  if (isLoading) return <div className="p-4 text-tg-hint">Завантаження…</div>;
  if (isError) return <div className="p-4 text-red-600">Не вдалося завантажити критерії.</div>;

  const weightTotal = criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
  const weightPct = (w: number): number =>
    weightTotal > 0 ? Math.round(((Number(w) || 0) / weightTotal) * 100) : 0;

  return (
    <div className="flex flex-col gap-4 p-4 pb-24">
      <Link to={`/`} className="text-xs text-tg-link">
        ← Назад
      </Link>
      <h1 className="text-lg font-semibold">Критерії оцінювання</h1>
      <p className="rounded-lg bg-tg-secondary p-2 text-xs text-tg-hint">
        <b>Вага</b> — наскільки критерій впливає на бал кандидата. Сума ваг усіх
        критеріїв = 100% (нормалізується при збереженні), напр. 0,22 ≈ 22%.
      </p>

      <div>
        <label className="block text-xs uppercase text-tg-hint">
          Поріг рекомендації (min_score)
        </label>
        <input
          type="range"
          min={1}
          max={10}
          step={0.5}
          value={minScore}
          onChange={(e) => setMinScore(Number(e.target.value))}
          className="w-full"
        />
        <div className="text-sm">Поточне: {minScore.toFixed(1)} / 10</div>
      </div>

      <ul className="flex flex-col gap-3">
        {criteria.map((c, idx) => (
          <li key={c.key} className="rounded border border-tg-secondary p-3">
            <div className="flex items-start justify-between gap-2">
              <input
                value={c.label}
                onChange={(e) =>
                  setCriteria((arr) => {
                    const next = [...arr];
                    next[idx] = { ...next[idx]!, label: e.target.value };
                    return next;
                  })
                }
                className="flex-1 rounded border border-tg-secondary bg-tg-bg px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={() => setCriteria((arr) => arr.filter((_, i) => i !== idx))}
                className="text-xs text-red-600"
              >
                Видалити
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-tg-hint">
              <span>Вага</span>
              <input
                type="number"
                step={0.05}
                min={0}
                max={1}
                value={c.weight}
                onChange={(e) =>
                  setCriteria((arr) => {
                    const next = [...arr];
                    next[idx] = { ...next[idx]!, weight: Number(e.target.value) };
                    return next;
                  })
                }
                className="w-20 rounded border border-tg-secondary bg-tg-bg px-2 py-1 text-sm"
              />
              <span>≈ {weightPct(c.weight)}% важливості</span>
            </div>
            <textarea
              rows={2}
              value={c.description}
              onChange={(e) =>
                setCriteria((arr) => {
                  const next = [...arr];
                  next[idx] = { ...next[idx]!, description: e.target.value };
                  return next;
                })
              }
              placeholder="Опис критерію (не обовʼязково)"
              className="mt-2 w-full rounded border border-tg-secondary bg-tg-bg p-2 text-sm"
            />
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="rounded border border-dashed border-tg-secondary p-2 text-sm text-tg-hint"
        onClick={() =>
          setCriteria((arr) => [
            ...arr,
            {
              key: `c${Date.now()}`,
              label: 'Новий критерій',
              weight: 0.1,
              description: '',
            },
          ])
        }
      >
        + Додати критерій
      </button>

      <button
        type="button"
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="fixed inset-x-4 bottom-20 rounded-lg bg-tg-button py-3 text-tg-buttonText disabled:opacity-50"
      >
        {save.isPending ? 'Зберігання…' : 'Зберегти і перерахувати'}
      </button>
    </div>
  );
}
